<script lang="ts" setup>
/**
 * @name Estadisticas.vue
 * @version 1.0.0
 * @description Vista para mostrar datos de bitácora de instituciones
 * @author
 * @changelog
 * 1.0.0 - 2024/01/01 - @usuario - Versión inicial
 */

import { ref, computed, onMounted, watch } from 'vue'
import { BitacoraData, TransactionData } from '@shared/types/base'
import { useStore } from 'vuex'
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore'
import { dbRegistros, auth } from '@/firebase/config'
import { EPlan, PLAN_OPTIONS } from '@shared/types/enums'
import { convertFirestoreTimestamp, formatPrice, formatNumber } from '@/components/utils/functions'
import BaseModal from '../BaseModal.vue'
import { descargarAcuseConsumo } from '@/components/utils/acuseUtils'
import { es } from 'date-fns/locale/es'
const store = useStore()
const currentInstitution = computed(() => store.getters['global/currentInstitution'])
const loading = ref(false)
const errorMessage = ref<string>('')
const downloadAcuseConsumo = ref(false)
const showErrorModal = ref(false)
const fechaDelEstadoDeCuenta = ref({ start: new Date(), end: new Date(new Date().setDate(new Date().getDate() + 7)) })
/**
 * @description Referencia para los datos de bitácora
 */
const estadisticas = ref<any | null>(null)
const bitacora = ref<BitacoraData | null>(null)

/**
 * @description Obtiene los datos de bitácora desde Firestore
 *
 * Con Custom Claims, las reglas de Firestore verifican automáticamente
 * si el usuario tiene permisos de administrador. Si el usuario no es admin,
 * Firestore rechazará la petición con permission-denied.
 */
const obtenerEstadisticas = async (idInstitution: string): Promise<any | null> => {
	try {
		loading.value = true
		errorMessage.value = ''

		if (!idInstitution) {
			console.warn('No se proporcionó ID de institución')
			return null
		}

		// Verificar que el usuario esté autenticado
		const currentUser = auth.currentUser
		if (!currentUser) {
			errorMessage.value = 'Error de autenticación. Por favor, inicia sesión nuevamente'
			return null
		}

		// Refrescar el token para asegurar que tenemos los custom claims actualizados
		await currentUser.getIdToken(true)

		// 1. Obtener la bitácora
		const bitacoraRef = doc(dbRegistros, 'bitacora', idInstitution)
		const bitacoraSnap = await getDoc(bitacoraRef)

		if (!bitacoraSnap.exists()) {
			errorMessage.value = 'Estadísticas no encontradas para esta institución'
			return null
		}

		const bitacoraData = bitacoraSnap.data() as BitacoraData

		// Convertir el Timestamp de Firestore a timestamp de JavaScript
		if (bitacoraData.modificationDate && typeof bitacoraData.modificationDate === 'object') {
			bitacoraData.modificationDate = convertFirestoreTimestamp(bitacoraData.modificationDate)
		}

		// 2. Obtener todas las transacciones de la institución
		const transaccionesRef = collection(dbRegistros, 'transacciones')
		const q = query(transaccionesRef, where('idInstitution', '==', idInstitution))
		const transaccionesSnapshot = await getDocs(q)

		// 3. Calcular las estadísticas
		let totalDecrementosTest = 0
		let totalDecrementosProduction = 0
		let totalAumentos = 0
		let totalGastado = 0
		let totalRecargado = 0

		for (const doc of transaccionesSnapshot.docs) {
			const trans = doc.data() as TransactionData

			if (trans.typeTransaction === 'decremento') {
				if (trans.environment === 'test') {
					totalDecrementosTest += trans.quantityDocuments || 0
				} else {
					totalDecrementosProduction += trans.quantityDocuments || 0
				}
				totalGastado += (trans.quantityDocuments || 0) * (trans.pricePerDocument || bitacoraData.pricePerDocument)
			} else if (trans.typeTransaction === 'aumento') {
				totalAumentos += trans.quantityDocuments || 0
				if (trans.pricePerDocument) {
					totalRecargado += (trans.quantityDocuments || 0) * (trans.pricePerDocument || bitacoraData.pricePerDocument)
				}
			}
		}

		const estadisticas = {
			totalTransacciones: transaccionesSnapshot.size,
			totalDecrementosTest: totalDecrementosTest,
			totalDecrementosProduction: totalDecrementosProduction,
			totalAumentos: totalAumentos,
			totalRecargado: totalRecargado,
			totalGastado: totalGastado
		}

		return {
			bitacora: {
				...bitacoraData,
				idInstitution: idInstitution
			},
			estadisticas: estadisticas
		}
	} catch (err: any) {
		console.error('Error al obtener estadísticas:', err)

		if (err.code === 'permission-denied') {
			errorMessage.value = 'No tienes permisos para acceder a esta bitácora. Se requieren permisos de administrador.'
		} else if (err.code === 'unauthenticated') {
			errorMessage.value = 'Error de autenticación. Por favor, inicia sesión nuevamente'
		} else {
			errorMessage.value = err.message || 'Error al cargar los datos de estadísticas'
		}

		return null
	} finally {
		loading.value = false
	}
}

/**
 * @description Carga los datos de bitácora cuando cambia la institución
 */
const cargarEstadisticas = async () => {
	if (!currentInstitution.value) {
		estadisticas.value = null
		return
	}

	const datos = await obtenerEstadisticas(currentInstitution.value)
	if (datos && datos.bitacora && datos.estadisticas) {
		estadisticas.value = datos.estadisticas
		bitacora.value = datos.bitacora
	} else {
		bitacora.value = {
			idInstitution: currentInstitution.value,
			institutionName: 'Institución',
			totalDocumentsProduction: 0,
			totalDocumentsTest: 0,
			modificationDate: Date.now(),
			LastUserWhomodified: 'N/A',
			pricePerDocument: 0,
			precioPlan: 0,
			iva: 0,
			fechaInicioContrato: Date.now(),
			fechaFinContrato: Date.now(),
			plan: EPlan.POSTPAID as EPlan
		}
		// Si no hay datos, usar valores por defecto
		estadisticas.value = {
			totalTransacciones: 0,
			totalDecrementosTest: 0,
			totalDecrementosProduction: 0,
			totalAumentos: 0,
			totalRecargado: 0,
			totalGastado: 0
		}
	}
}

// Cargar datos cuando el componente se monte
onMounted(() => {
	cargarEstadisticas()
})

// Observar cambios en la institución actual
watch(
	() => currentInstitution.value,
	() => {
		cargarEstadisticas()
	}
)
const descargar = async (plan: EPlan, currentInstitution: string, bitacora: BitacoraData, fechaDelEstadoDeCuenta: { start: Date; end: Date }) => {
	downloadAcuseConsumo.value = true
	console.log(fechaDelEstadoDeCuenta)
	await descargarAcuseConsumo(plan, currentInstitution, bitacora, fechaDelEstadoDeCuenta)
		.catch(error => {
			console.error('Error al descargar acuse de consumo:', error)
			errorMessage.value = error.message
			showErrorModal.value = true
		})
		.finally(() => {
			downloadAcuseConsumo.value = false
			loading.value = false
		})
}
const closeErrorModal = () => {
	showErrorModal.value = false
	errorMessage.value = ''
}
</script>

<template>
	<!-- Estado de carga -->
	<div v-if="loading" class="loading-state">
		<div class="loader"></div>
		<p class="loading-text">Cargando datos de bitácora...</p>
	</div>

	<!-- Estado de descarga -->
	<div v-if="downloadAcuseConsumo" class="loading-state">
		<div class="loader"></div>
		<p class="loading-text">Descargando acuse de consumo...</p>
	</div>

	<!-- Contenido principal -->
	<VaCard v-if="estadisticas && !loading && !errorMessage && !downloadAcuseConsumo" class="bitacora-card">
		<va-card-title class="header-gradient">
			<div class="header-content">
				<div class="header-main">
					<div class="header-icon-wrapper">
						<va-icon name="account_balance" class="header-icon" />
					</div>
					<div class="header-text">
						<h2 class="header-title">{{ bitacora?.institutionName }}</h2>
						<p class="header-subtitle">ID: {{ bitacora?.idInstitution }}</p>
						<p class="header-subtitle">Plan: {{ PLAN_OPTIONS.find(option => option.value === bitacora?.plan)?.text }}</p>
					</div>
				</div>
			</div>
		</va-card-title>
		<va-card-content class="card-content">
			<!-- Sección de Resumen de Transacciones -->
			<VaCard class="summary-hero-card">
				<div class="summary-hero-accent"></div>
				<VaCardContent class="summary-hero-content">
					<div class="summary-hero-header">
						<div class="summary-hero-icon-wrapper">
							<va-icon name="receipt_long" class="summary-hero-icon" />
						</div>
						<div class="summary-hero-text">
							<h2 class="summary-hero-title">Resumen de Transacciones</h2>
							<p class="summary-hero-subtitle">Consulta el total de movimientos y descarga el acuse de consumo del periodo</p>
						</div>
					</div>

					<div v-if="bitacora?.plan === EPlan.UNLIMITED || bitacora?.plan === EPlan.PREPAID || bitacora?.plan === EPlan.BASIC" class="summary-hero-actions">
						<div
							class="summary-hero-date"
							style="justify-content: center !important; align-items: center !important; width: 100% !important; display: flex !important; flex-direction: column !important; gap: 0.5rem !important">
							<label class="summary-hero-date-label" for="bitacora-fecha-fin">Seleccionar mes del acuse de consumo</label>
							<VaDatePicker id="bitacora-fecha-fin" v-model="fechaDelEstadoDeCuenta" mode="range" :locale="es" class="balance-date-picker" auto-apply :teleport="true" />
							<va-button
								icon="download"
								class="descargar-acuse-consumo"
								color="success"
								size="large"
								@click="descargar(bitacora?.plan || EPlan.POSTPAID, currentInstitution, bitacora, fechaDelEstadoDeCuenta)">
								Descargar acuse de consumo
							</va-button>
						</div>
					</div>
					<div class="package-grid">
						<div class="package-card">
							<div class="card-gradient transaction-gradient"></div>
							<div class="card-content-wrapper">
								<div class="card-header">
									<div class="package-avatar transaction-avatar">
										<va-icon name="receipt_long" class="package-icon" />
									</div>
									<div class="card-title-group">
										<h3 class="card-title">Total transacciones</h3>
										<p class="card-subtitle">Transacciones registradas</p>
									</div>
								</div>
								<div class="card-value transaction-value">
									{{ formatNumber(estadisticas?.totalTransacciones) }}
								</div>
							</div>
						</div>

						<div v-if="bitacora?.plan === EPlan.PREPAID" class="package-card increase-card">
							<div class="card-gradient increase-gradient"></div>
							<div class="card-content-wrapper">
								<div class="card-header">
									<div class="package-avatar increase-avatar">
										<va-icon name="trending_up" class="package-icon" />
									</div>
									<div class="card-title-group">
										<h3 class="card-title">Total aumentos</h3>
										<p class="card-subtitle">Documentos agregados</p>
									</div>
								</div>
								<div class="card-value increase-value">
									{{ formatNumber(estadisticas?.totalAumentos) }}
								</div>
							</div>
						</div>

						<div v-if="bitacora?.plan !== EPlan.UNLIMITED && bitacora?.plan !== EPlan.BASIC" class="package-card decrease-card">
							<div class="card-gradient decrease-production-gradient"></div>
							<div class="card-content-wrapper">
								<div class="card-header">
									<div class="package-avatar decrease-avatar-production">
										<va-icon name="trending_down" class="package-icon" />
									</div>
									<div class="card-title-group">
										<h3 class="card-title">Total de documentos consumidos en modo producción</h3>
										<p class="card-subtitle">Documentos consumidos</p>
									</div>
								</div>
								<div class="card-value decrease-production-value">
									{{ formatNumber(estadisticas?.totalDecrementosProduction) }}
								</div>
							</div>
						</div>
						<div v-if="bitacora?.plan !== EPlan.UNLIMITED && bitacora?.plan !== EPlan.BASIC" class="package-card decrease-card">
							<div class="card-gradient decrease-test-gradient"></div>
							<div class="card-content-wrapper">
								<div class="card-header">
									<div class="package-avatar decrease-avatar-test">
										<va-icon name="trending_down" class="package-icon" />
									</div>
									<div class="card-title-group">
										<h3 class="card-title">Total de documentos consumidos en modo pruebas</h3>
										<p class="card-subtitle">Documentos consumidos</p>
									</div>
								</div>
								<div class="card-value decrease-test-value">
									{{ formatNumber(estadisticas?.totalDecrementosTest) }}
								</div>
							</div>
						</div>
					</div>
				</VaCardContent>
			</VaCard>

			<!-- Sección de Información Financiera -->
			<div v-if="bitacora?.plan === EPlan.POSTPAID" class="section-title section-spacing">
				<div class="section-title-content">
					<va-icon name="account_balance_wallet" class="title-icon" />
					<span>Información Financiera</span>
				</div>
			</div>

			<div v-if="bitacora?.plan === EPlan.POSTPAID" class="package-card balance-card">
				<div class="card-content-wrapper">
					<div class="balance-header">
						<div class="balance-icon-wrapper">
							<va-icon :name="(estadisticas?.totalGastado || 0) >= 0 ? 'check_circle' : 'warning'" class="balance-icon" />
						</div>
						<div class="balance-content">
							<h3 class="balance-title">Total a cobrar</h3>
							<p class="balance-subtitle">Total de efectivo gastado por la institución por documentos consumidos</p>
							<div class="row">
								<div class="col-md-8" style="display: flex; justify-content: flex-end"></div>
								<div class="col-md-4" style="display: flex; justify-content: flex-end; margin-top: 1rem">
									<div style="width: 100%; display: flex; flex-direction: column; gap: 0.5rem">
										<label class="balance-date-label" for="bitacora-fecha-fin">Seleccionar mes</label>
										<VaDatePicker v-model="fechaDelEstadoDeCuenta" mode="range" color="warning" :locale="es" class="balance-date-picker-full" auto-apply :teleport="true" />
										<va-button
											icon="download"
											class="descargar-acuse-consumo-full"
											color="success"
											@click="descargar(bitacora?.plan || EPlan.POSTPAID, currentInstitution, bitacora, fechaDelEstadoDeCuenta)">
											Descargar acuse de consumo
										</va-button>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div :class="['balance-value balance-negative']">
						{{ formatPrice(estadisticas?.totalGastado) }}
					</div>
					<div class="balance-indicator">
						<div :class="['indicator-bar indicator-negative']"></div>
					</div>
				</div>
			</div>
		</va-card-content>
	</VaCard>
	<!-- Estado vacío -->
	<div v-if="!estadisticas && !loading && !errorMessage" class="empty-state">
		<div class="empty-icon-wrapper">
			<va-icon name="info" class="empty-icon" />
		</div>
		<h3 class="empty-title">No hay datos de bitácora</h3>
		<p class="empty-description">No se encontraron datos de bitácora para mostrar</p>
	</div>

	<BaseModal v-model="showErrorModal" title="Error" :message="errorMessage" :testMode="false" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ errorMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="closeErrorModal">Entendido</button>
		</template>
	</BaseModal>
</template>
<style scoped>
.balance-date-picker {
	width: 40% !important;
	background-color: #fff;
	border: 1px solid #e0e0e0;
	border-radius: 10px;
	padding: 10px;
	font-size: 16px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.3s ease;
	box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
	color: #000;
}
.balance-date-picker-full {
	width: 100%;
	background-color: #fff;
	border: 1px solid #e0e0e0;
	border-radius: 10px;
	padding: 10px;
	font-size: 16px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.3s ease;
	box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
	color: #000;
}
.descargar-acuse-consumo {
	border: none;
	border-radius: 12px;
	padding: 12px 22px;
	font-size: 0.95rem;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.3s ease;
	box-shadow: 0 4px 14px rgba(34, 197, 94, 0.35);
	flex-shrink: 0;
	white-space: nowrap;
	width: 40% !important;
	margin-top: 1rem;
	margin-bottom: 1rem;
}
.descargar-acuse-consumo-full {
	border: none;
	border-radius: 12px;
	padding: 12px 22px;
	font-size: 0.95rem;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.3s ease;
	box-shadow: 0 4px 14px rgba(34, 197, 94, 0.35);
	flex-shrink: 0;
	white-space: nowrap;
	width: 100% !important;
	margin-top: 1rem;
	margin-bottom: 1rem;
}
.descargar-acuse-consumo:hover {
	transform: translateY(-1px);
	box-shadow: 0 6px 18px rgba(34, 197, 94, 0.45);
}
.bitacora-card {
	border-radius: 20px;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
	animation: fadeInUp 0.6s ease-out;
	overflow: hidden;
	background: white;
	border: none;
	width: 100%;
	display: flex;
	flex-direction: column;
}

/* Header mejorado */
.header-gradient {
	background: #062342;
	padding: 0.75rem;
	border-radius: 0;
	position: relative;
	flex-shrink: 0;
}

.header-gradient::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
	pointer-events: none;
}

.header-content {
	position: relative;
	z-index: 1;
}

.header-main {
	display: flex;
	align-items: center;
	gap: 1.5rem;
}

.header-icon-wrapper {
	width: 64px;
	height: 64px;
	border-radius: 16px;
	background: rgba(255, 255, 255, 0.2);
	backdrop-filter: blur(10px);
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
	transition: all 0.3s ease;
}

.header-icon-wrapper:hover {
	transform: scale(1.1) rotate(5deg);
	background: rgba(255, 255, 255, 0.3);
}

.header-icon {
	color: white;
	font-size: 2rem;
}

.header-text {
	flex: 1;
}

.header-title {
	font-size: 1.75rem;
	font-weight: 700;
	color: white;
	margin: 0;
	text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
	letter-spacing: -0.5px;
}

.header-subtitle {
	font-size: 1rem;
	color: rgba(255, 255, 255, 0.9);
	margin: 0.5rem 0 0 0;
	font-weight: 500;
}

.card-content {
	padding: 2.5rem;
	padding-bottom: 3rem;
	background: #fafbfc;
	overflow-y: auto;
	max-height: calc(100vh - 200px);
}

/* Scrollbar personalizado para el contenido */
.card-content::-webkit-scrollbar {
	width: 8px;
}

.card-content::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 4px;
}

.card-content::-webkit-scrollbar-thumb {
	background: #888;
	border-radius: 4px;
}

.card-content::-webkit-scrollbar-thumb:hover {
	background: #555;
}

/* Secciones */
.section-title {
	margin-bottom: 2rem;
	padding-bottom: 1rem;
	border-bottom: 3px solid transparent;
	border-image: #062342 1;
	animation: slideInLeft 0.5s ease-out;
}

.section-title-content {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	font-size: 1.5rem;
	font-weight: 700;
	color: #1a202c;
	letter-spacing: -0.5px;
}

.title-icon {
	color: #062342;
	font-size: 1.75rem;
	animation: pulse 2s ease-in-out infinite;
}

/* Card hero de resumen */
.summary-hero-card {
	position: relative;
	width: 100%;
	margin-bottom: 1.75rem;
	border-radius: 18px !important;
	border: 1px solid rgba(6, 35, 66, 0.08) !important;
	overflow: hidden;
	box-shadow: 0 8px 28px rgba(6, 35, 66, 0.12) !important;
	background: linear-gradient(135deg, #ffffff 0%, #f3f7fb 55%, #e8f1f8 100%) !important;
	animation: fadeInUp 0.55s ease-out;
}

.summary-hero-accent {
	position: absolute;
	inset: 0 0 auto 0;
	height: 5px;
	background: linear-gradient(90deg, #062342 0%, #169bff 55%, #24c8ff 100%);
}

.summary-hero-content {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	padding: 1.75rem 1.75rem 1.5rem !important;
}

.summary-hero-header {
	display: flex;
	align-items: center;
	gap: 1.25rem;
}

.summary-hero-icon-wrapper {
	width: 72px;
	height: 72px;
	border-radius: 18px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	background: linear-gradient(135deg, #062342 0%, #0c4a6e 100%);
	box-shadow: 0 8px 22px rgba(6, 35, 66, 0.28);
}

.summary-hero-icon {
	color: #24c8ff;
	font-size: 2.1rem;
}

.summary-hero-text {
	flex: 1;
	min-width: 0;
}

.summary-hero-title {
	margin: 0;
	font-size: clamp(1.35rem, 2.2vw, 1.85rem);
	font-weight: 800;
	color: #062342;
	letter-spacing: -0.03em;
	line-height: 1.2;
}

.summary-hero-subtitle {
	margin: 0.45rem 0 0;
	font-size: 0.95rem;
	font-weight: 500;
	color: rgba(6, 35, 66, 0.68);
	line-height: 1.45;
	max-width: 42rem;
}

.summary-hero-date-label {
	margin: 0;
	font-size: 1.2rem;
	font-weight: 600;
	margin-bottom: 1rem;
	color: rgba(6, 35, 66, 0.82);
	letter-spacing: 0.02em;
}

.summary-hero-date :deep(.balance-date-picker),
.summary-hero-date :deep(.va-input-wrapper),
.summary-hero-date :deep(.dp__main) {
	width: 100%;
}

/* Grid de tarjetas */
.package-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
	gap: 1.5rem;
}

/* Tarjetas base */
.package-card {
	position: relative;
	background: white;
	border-radius: 16px;
	padding: 0;
	transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
	overflow: hidden;
	animation: fadeInScale 0.6s ease-out;
	border: 1px solid rgba(0, 0, 0, 0.05);
}

.package-card::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 4px;
	background: linear-gradient(90deg, transparent 0%, rgba(102, 126, 234, 0.5) 50%, transparent 100%);
	opacity: 0;
	transition: opacity 0.3s ease;
}

.package-card:hover {
	transform: translateY(-8px) scale(1.02);
	box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.package-card:hover::before {
	opacity: 1;
}

/* Gradientes de tarjetas */
.card-gradient {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 120px;
	border-radius: 16px 16px 0 0;
	opacity: 0.15;
	transition: opacity 0.3s ease;
}

.transaction-gradient {
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.increase-gradient {
	background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.decrease-production-gradient {
	background: linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%);
}
.decrease-test-gradient {
	background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}
.package-card:hover .card-gradient {
	opacity: 0.25;
}

.card-content-wrapper {
	position: relative;
	padding: 1.75rem;
	z-index: 1;
}

.card-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
}

.card-title-group {
	flex: 1;
}

.card-title {
	font-size: 1.125rem;
	font-weight: 700;
	color: #1a202c;
	margin: 0 0 0.25rem 0;
	letter-spacing: -0.3px;
}

.card-subtitle {
	font-size: 0.875rem;
	color: #718096;
	margin: 0;
	font-weight: 500;
}

/* Avatares */
.package-avatar {
	width: 56px;
	height: 56px;
	border-radius: 14px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
	transition: all 0.3s ease;
	position: relative;
	overflow: hidden;
}

.package-avatar::before {
	content: '';
	position: absolute;
	top: -50%;
	left: -50%;
	width: 200%;
	height: 200%;
	background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.3), transparent);
	transform: rotate(45deg);
	transition: all 0.5s ease;
}

.package-card:hover .package-avatar::before {
	animation: shine 0.6s ease;
}

.transaction-avatar {
	background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
}

.increase-avatar {
	background: linear-gradient(90deg, #10b981 0%, #059669 100%);
}

.decrease-avatar-production {
	background: linear-gradient(90deg, #2563eb 0%, #1e3a8a 100%);
}
.decrease-avatar-test {
	background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
}

.package-icon {
	color: white;
	font-size: 1.5rem;
	position: relative;
	z-index: 1;
}

/* Valores de las tarjetas */
.card-value {
	font-size: 2.5rem;
	font-weight: 800;
	letter-spacing: -1px;
	margin-top: 0.5rem;
	transition: all 0.3s ease;
}

.transaction-value {
	color: #000000;
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
}

.increase-value {
	color: #000000;
	background: linear-gradient(135deg, #10b981 0%, #059669 100%);
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
}

.decrease-production-value {
	color: #ffffff;
	background: linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%);
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
}
.decrease-test-value {
	color: #000000;
	background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
}

/* Tarjeta de Balance Neto - Destacada */
.balance-card {
	background: linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%);
	color: white;
	position: relative;
	overflow: hidden;
}

.balance-card::before {
	content: '';
	position: absolute;
	top: -50%;
	right: -50%;
	width: 200%;
	height: 200%;
	background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
	animation: rotate 20s linear infinite;
}

.balance-card .card-content-wrapper {
	padding: 2.5rem;
	position: relative;
	z-index: 1;
}

.balance-header {
	display: flex;
	align-items: center;
	gap: 1.5rem;
	margin-bottom: 2rem;
}

.balance-icon-wrapper {
	width: 80px;
	height: 80px;
	border-radius: 20px;
	background: rgba(255, 255, 255, 0.2);
	backdrop-filter: blur(10px);
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
	transition: all 0.3s ease;
}

.balance-card:hover .balance-icon-wrapper {
	transform: scale(1.1) rotate(5deg);
	background: rgba(255, 255, 255, 0.3);
}

.balance-icon {
	color: white;
	font-size: 2.5rem;
}

.balance-content {
	flex: 1;
}

.balance-title {
	font-size: 1.5rem;
	font-weight: 700;
	color: white;
	margin: 0 0 0.5rem 0;
	letter-spacing: -0.5px;
}

.balance-subtitle {
	font-size: 1rem;
	color: rgba(255, 255, 255, 0.9);
	margin: 0;
	font-weight: 500;
}

.balance-value {
	font-size: 3.5rem;
	font-weight: 800;
	text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
	letter-spacing: -1px;
	margin-bottom: 1.5rem;
	transition: all 0.3s ease;
}

.balance-negative {
	color: #fbbf24;
	text-shadow: 0 2px 10px rgba(251, 191, 36, 0.3);
}

.balance-indicator {
	width: 100%;
	height: 6px;
	background: rgba(255, 255, 255, 0.2);
	border-radius: 3px;
	overflow: hidden;
}

.indicator-bar {
	height: 100%;
	border-radius: 3px;
	transition: width 0.5s ease;
	width: 100%;
}

.indicator-negative {
	background: linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%);
	box-shadow: 0 0 10px rgba(251, 191, 36, 0.5);
}

.section-spacing {
	margin-top: 3rem;
}

@keyframes rotate {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

/* Estado de carga */
.loading-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 4rem 2rem;
	text-align: center;
	background: white;
	border-radius: 20px;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
	animation: fadeInUp 0.6s ease-out;
}

.loader {
	border: 4px solid #f3f4f6;
	border-radius: 50%;
	border-top: 4px solid #2563eb;
	width: 60px;
	height: 60px;
	animation: spin 1s linear infinite;
	box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
	margin-bottom: 1.5rem;
}

.loading-text {
	margin-top: 1rem;
	color: #6c757d;
	font-size: 1rem;
	font-weight: 500;
}

/* Estado de error */

.error-icon {
	font-size: 4rem;
	color: #dc2626;
	margin-bottom: 1rem;
}

.error-title {
	font-size: 1.75rem;
	font-weight: 700;
	color: #1a202c;
	margin-bottom: 0.75rem;
	letter-spacing: -0.5px;
}

.error-description {
	color: #718096;
	font-size: 1.125rem;
	font-weight: 500;
}

/* Estado vacío mejorado */
.empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 4rem 2rem;
	text-align: center;
	background: white;
	border-radius: 20px;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
	animation: fadeInUp 0.6s ease-out;
}

.empty-icon-wrapper {
	width: 120px;
	height: 120px;
	border-radius: 50%;
	background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 2rem;
	animation: float 3s ease-in-out infinite;
}

.empty-icon {
	font-size: 4rem;
	color: #667eea;
}

.empty-title {
	font-size: 1.75rem;
	font-weight: 700;
	color: #1a202c;
	margin-bottom: 0.75rem;
	letter-spacing: -0.5px;
}

.empty-description {
	color: #718096;
	font-size: 1.125rem;
	font-weight: 500;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes fadeInScale {
	from {
		opacity: 0;
		transform: scale(0.9);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

@keyframes slideInLeft {
	from {
		opacity: 0;
		transform: translateX(-20px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

@keyframes pulse {
	0%,
	100% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
}

@keyframes shine {
	0% {
		transform: translateX(-100%) translateY(-100%) rotate(45deg);
	}
	100% {
		transform: translateX(100%) translateY(100%) rotate(45deg);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

/* Responsive */
@media (max-width: 1024px) {
	.package-grid {
		grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
		gap: 1.25rem;
	}
}

@media (max-width: 768px) {
	.card-content {
		padding: 1.5rem;
		padding-bottom: 2rem;
	}
	.header-gradient {
		padding: 1.5rem;
	}

	.header-main {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
	}

	.header-icon-wrapper {
		width: 56px;
		height: 56px;
	}

	.header-icon {
		font-size: 1.75rem;
	}

	.header-title {
		font-size: 1.5rem;
	}

	.section-title-content {
		font-size: 1.25rem;
	}

	.summary-hero-content {
		padding: 1.35rem 1.25rem 1.25rem !important;
		gap: 1.15rem;
	}

	.summary-hero-header {
		gap: 1rem;
	}

	.summary-hero-icon-wrapper {
		width: 60px;
		height: 60px;
		border-radius: 14px;
	}

	.summary-hero-icon {
		font-size: 1.75rem;
	}

	.summary-hero-actions {
		flex-direction: column;
		align-items: stretch;
		width: 100%;
	}

	.summary-hero-date {
		width: 100%;
	}

	.descargar-acuse-consumo {
		width: 100%;
		white-space: normal;
	}

	.package-grid {
		grid-template-columns: 1fr;
		gap: 1rem;
	}

	.card-value {
		font-size: 2rem;
	}

	.balance-value {
		font-size: 2.5rem;
	}

	.balance-card .card-content-wrapper {
		padding: 1.5rem;
	}

	.section-spacing {
		margin-top: 2rem;
	}
}

@media (max-width: 480px) {
	.card-content {
		padding: 1.25rem;
		padding-bottom: 1.75rem;
	}

	.header-gradient {
		padding: 1.25rem;
	}

	.card-content-wrapper {
		padding: 1.25rem;
	}

	.card-value {
		font-size: 1.75rem;
	}

	.balance-value {
		font-size: 2rem;
	}

	.balance-card .card-content-wrapper {
		padding: 1.25rem;
	}

	.balance-icon-wrapper {
		width: 64px;
		height: 64px;
	}

	.balance-icon {
		font-size: 2rem;
	}

	.balance-title {
		font-size: 1.25rem;
	}
}
</style>
